import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import Icon from '../components/Icon';
import { openContact } from '../utils/contact';
import './Home.css';

function useFadeIn() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('visible');
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

// ── Hero ────────────────────────────────────────────────────────────────────
function HeroDashboardMockup() {
  return (
    <div className="hero-mockup" aria-hidden="true">
      <div className="hero-mockup-bar">
        <span className="hero-mockup-dot" />
        <span className="hero-mockup-dot" />
        <span className="hero-mockup-dot" />
        <span className="hero-mockup-title">Residencial Aurora · Dashboard</span>
      </div>
      <div className="hero-mockup-body">
        <div className="hero-kpis">
          <div className="hero-kpi">
            <span className="hero-kpi-label">VGV</span>
            <span className="hero-kpi-value">R$ 48,2 mi</span>
          </div>
          <div className="hero-kpi">
            <span className="hero-kpi-label">Vendido</span>
            <span className="hero-kpi-value">72%</span>
          </div>
          <div className="hero-kpi">
            <span className="hero-kpi-label">Obra</span>
            <span className="hero-kpi-value">58%</span>
          </div>
        </div>

        <div className="hero-progress">
          <div className="hero-progress-row">
            <span>Fundacao</span>
            <div className="hero-bar"><i style={{ width: '100%' }} className="ok" /></div>
            <span className="hero-progress-pct">100%</span>
          </div>
          <div className="hero-progress-row">
            <span>Estrutura</span>
            <div className="hero-bar"><i style={{ width: '85%' }} className="ok" /></div>
            <span className="hero-progress-pct">85%</span>
          </div>
          <div className="hero-progress-row">
            <span>Alvenaria</span>
            <div className="hero-bar"><i style={{ width: '40%' }} className="warn" /></div>
            <span className="hero-progress-pct">40%</span>
          </div>
          <div className="hero-progress-row">
            <span>Acabamento</span>
            <div className="hero-bar"><i style={{ width: '8%' }} /></div>
            <span className="hero-progress-pct">8%</span>
          </div>
        </div>

        <div className="hero-mockup-foot">
          <span className="hero-chip hero-chip-green">Fluxo de caixa positivo</span>
          <span className="hero-chip">3 parcelas a vencer</span>
        </div>
      </div>
    </div>
  );
}

function HeroSection({ t }) {
  const trust = t('hero.trustChips', { returnObjects: true });
  return (
    <section className="hero">
      <div className="hero-bg-glow" aria-hidden="true" />
      <div className="hero-inner">
        <div className="hero-badge">{t('hero.badge')}</div>
        <h1 className="hero-title">
          {t('hero.title')}{' '}
          <span className="gradient-text">{t('hero.titleHighlight')}</span>
        </h1>
        <p className="hero-subtitle">{t('hero.subtitle')}</p>
        <div className="hero-actions">
          <button type="button" onClick={openContact} className="btn-hero-primary">{t('hero.cta')}</button>
          <a href="#modules" className="btn-hero-ghost">{t('hero.ctaSecondary')} →</a>
        </div>
        <p className="hero-note">{t('hero.trialNote')}</p>
        {Array.isArray(trust) && (
          <ul className="hero-trust">
            {trust.map((chip, i) => (
              <li key={i} className="hero-trust-item">
                <span className="hero-trust-check" aria-hidden="true">✓</span>{chip}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="hero-visual">
        <HeroDashboardMockup />
      </div>
    </section>
  );
}

// ── Metricas ──────────────────────────────────────────────────────────────────
function MetricsSection({ t }) {
  const ref = useFadeIn();
  const metrics = t('metrics.items', { returnObjects: true });
  if (!Array.isArray(metrics)) return null;
  return (
    <section className="metrics-section fade-section" ref={ref}>
      <div className="metrics-inner">
        <div className="metrics-header">
          <h2>{t('metrics.title')}</h2>
          <p>{t('metrics.subtitle')}</p>
        </div>
        <div className="metrics-grid">
          {metrics.map((m, i) => (
            <div key={i} className="metric-card">
              <span className="metric-value">{m.value}</span>
              <span className="metric-label">{m.label}</span>
            </div>
          ))}
        </div>
        <p className="metrics-footnote">{t('metrics.footnote')}</p>
      </div>
    </section>
  );
}

// ── Modulos ─────────────────────────────────────────────────────────────────
function ModulesSection({ t }) {
  const ref = useFadeIn();
  const items = t('modules.items', { returnObjects: true });
  if (!Array.isArray(items)) return null;
  return (
    <section className="section fade-section" id="modules" ref={ref}>
      <div className="section-inner">
        <div className="section-header">
          <h2>{t('modules.title')}</h2>
          <p>{t('modules.subtitle')}</p>
        </div>
        <div className="cards-grid">
          {items.map((item, i) => (
            <div key={i} className="info-card">
              <div className="info-card-icon"><Icon name={item.icon} size={26} /></div>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Como funciona ─────────────────────────────────────────────────────────────
function HowItWorksSection({ t }) {
  const ref = useFadeIn();
  const steps = t('howItWorks.steps', { returnObjects: true });
  if (!Array.isArray(steps)) return null;
  return (
    <section className="section section-dark fade-section" id="how-it-works" ref={ref}>
      <div className="section-inner">
        <div className="section-header">
          <h2>{t('howItWorks.title')}</h2>
          <p>{t('howItWorks.subtitle')}</p>
        </div>
        <div className="steps-grid">
          {steps.map((step) => (
            <div key={step.number} className="step-card">
              <div className="step-number">{step.number}</div>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Recursos (diferenciais) ───────────────────────────────────────────────────
function FeaturesSection({ t }) {
  const ref = useFadeIn();
  const items = t('features.items', { returnObjects: true });
  if (!Array.isArray(items)) return null;
  return (
    <section className="section fade-section" id="features" ref={ref}>
      <div className="section-inner">
        <div className="section-header">
          <h2>{t('features.title')}</h2>
          <p>{t('features.subtitle')}</p>
        </div>
        <div className="cards-grid">
          {items.map((item, i) => (
            <div key={i} className="info-card">
              <div className="info-card-icon info-card-icon-orange"><Icon name={item.icon} size={26} /></div>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Perfis ────────────────────────────────────────────────────────────────────
function SegmentsSection({ t }) {
  const ref = useFadeIn();
  const items = t('segments.items', { returnObjects: true });
  if (!Array.isArray(items)) return null;
  return (
    <section className="section section-dark fade-section" id="segments" ref={ref}>
      <div className="section-inner">
        <div className="section-header">
          <h2>{t('segments.title')}</h2>
          <p>{t('segments.subtitle')}</p>
        </div>
        <div className="segments-grid">
          {items.map((seg, i) => (
            <div key={i} className="segment-card">
              <span className="segment-icon" aria-hidden="true">{seg.icon}</span>
              <h3 className="segment-name">{seg.name}</h3>
              <p className="segment-desc">{seg.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Planos ────────────────────────────────────────────────────────────────────
function PricingSection({ t }) {
  const ref = useFadeIn();
  const plans = t('pricing.plans', { returnObjects: true });
  if (!Array.isArray(plans)) return null;
  return (
    <section className="section fade-section" id="pricing" ref={ref}>
      <div className="section-inner">
        <div className="section-header">
          <h2>{t('pricing.title')}</h2>
          <p>{t('pricing.subtitle')}</p>
        </div>
        <div className="pricing-grid">
          {plans.map((plan, i) => {
            const highlighted = i === 1;
            return (
              <div key={plan.id} className={`plan-card ${highlighted ? 'plan-card-highlight' : ''}`}>
                {highlighted && <span className="plan-badge">{t('pricing.popular')}</span>}
                <h3 className="plan-name">{plan.name}</h3>
                <p className="plan-tagline">{plan.tagline}</p>
                <div className="plan-price">
                  <span className="plan-price-value">{plan.price}</span>
                  {plan.price.startsWith('R$') && <span className="plan-price-period">{t('pricing.perMonth')}</span>}
                </div>
                <ul className="plan-features">
                  {plan.features.map((f, fi) => (
                    <li key={fi}><span className="plan-check" aria-hidden="true">✓</span>{f}</li>
                  ))}
                </ul>
                <button type="button" onClick={openContact} className="plan-cta">{t('pricing.cta')}</button>
              </div>
            );
          })}
        </div>
        <p className="pricing-note">{t('pricing.note')}</p>
      </div>
    </section>
  );
}

// ── FAQ ───────────────────────────────────────────────────────────────────────
function FaqSection({ t }) {
  const ref = useFadeIn();
  const [open, setOpen] = useState(null);
  const items = t('faq.items', { returnObjects: true });
  const toggle = useCallback((i) => setOpen((prev) => (prev === i ? null : i)), []);
  if (!Array.isArray(items)) return null;
  return (
    <section className="section section-dark fade-section" id="faq" ref={ref}>
      <div className="section-inner">
        <div className="section-header"><h2>{t('faq.title')}</h2></div>
        <div className="faq-list" role="list">
          {items.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className={`faq-item ${isOpen ? 'open' : ''}`} role="listitem">
                <button
                  className="faq-question"
                  onClick={() => toggle(i)}
                  aria-expanded={isOpen}
                  aria-controls={`faq-panel-${i}`}
                >
                  {item.question}
                  <span className="faq-chevron" aria-hidden="true">{isOpen ? '−' : '+'}</span>
                </button>
                <div id={`faq-panel-${i}`} role="region" hidden={!isOpen}>
                  <p className="faq-answer">{item.answer}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ── CTA final ───────────────────────────────────────────────────────────────
function CtaSection({ t }) {
  const ref = useFadeIn();
  return (
    <section className="cta-section fade-section" ref={ref}>
      <div className="cta-inner">
        <h2>{t('cta.title')}</h2>
        <p>{t('cta.subtitle')}</p>
        <button type="button" onClick={openContact} className="btn-cta">{t('cta.button')}</button>
        <p className="cta-note">{t('cta.note')}</p>
      </div>
    </section>
  );
}

// ── Pagina ────────────────────────────────────────────────────────────────────
export default function Home() {
  const { t } = useTranslation();
  return (
    <>
      <Helmet>
        <title>LibbenIncorp — Gestao integral para incorporadoras</title>
        <meta name="description" content={t('hero.subtitle')} />
        <meta property="og:title" content="LibbenIncorp — Gestao integral para incorporadoras" />
        <meta property="og:description" content={t('hero.subtitle')} />
        <link rel="canonical" href="https://www.libbenincorp.com/" />
      </Helmet>
      <main>
        <HeroSection t={t} />
        <MetricsSection t={t} />
        <ModulesSection t={t} />
        <HowItWorksSection t={t} />
        <FeaturesSection t={t} />
        <SegmentsSection t={t} />
        <PricingSection t={t} />
        <FaqSection t={t} />
        <CtaSection t={t} />
      </main>
    </>
  );
}
